import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApolloQueryResult } from 'apollo-client';

import { IPluDataQuery } from 'generated/rbi-graphql';
import { IPosDataQuery, PosDataDocument } from 'generated/sanity-graphql';
import { useApolloClient } from 'state/graphql/hooks/use-sanity-query';
import { useServiceModeContext } from 'state/service-mode';
import { transformPlus } from 'utils/menu';
import { useSanityGqlEndpoint } from 'utils/network';
import { parseStringifiedJSON } from 'utils/parse-string';

import getRestaurantPosDataPlus from './get-restaurant-pos-data-plus';

type PosDataResults = {
  posData: Record<string, number> | null;
};

type PosDataIds = (ids?: {
  restaurantPosDataId: string | null;
  storeNumber: string | null;
}) => Promise<PosDataResults | null>;

interface IUsePosData {
  data: PosDataResults;
  refetch: PosDataIds;
}

const transformResponse = ({
  queryResponse,
  isDelivery,
}: {
  queryResponse: ApolloQueryResult<IPluDataQuery & IPosDataQuery>;
  isDelivery: boolean;
}): PosDataResults => {
  let rawPlus = null;

  rawPlus = getRestaurantPosDataPlus(queryResponse?.data, isDelivery);

  if (!rawPlus) {
    return { posData: null };
  }

  return {
    posData: transformPlus(parseStringifiedJSON({ defaultValue: {}, value: rawPlus })),
  };
};

export const usePosDataQuery = ({
  restaurantPosDataId,
  storeNumber,
  lazy = false,
}: {
  restaurantPosDataId: string | null;
  storeNumber: string | null;
  lazy?: boolean;
}): IUsePosData => {
  const { isDelivery } = useServiceModeContext();
  const sanityUri = useSanityGqlEndpoint();
  const client = useApolloClient();

  const [data, setData] = useState<PosDataResults>({
    posData: null,
  });

  const fetchPosData: PosDataIds = useCallback(
    async ids => {
      const id = ids?.restaurantPosDataId ?? restaurantPosDataId;

      if (!id) {
        return Promise.resolve({ posData: null });
      }

      const queryResponse = await client.query({
        context: { uri: sanityUri },
        query: PosDataDocument,
        variables: {
          id,
        },
      });

      let transformedResponse: PosDataResults = transformResponse({
        queryResponse,
        isDelivery,
      });

      setData(transformedResponse);
      return transformedResponse;
    },
    [client, isDelivery, restaurantPosDataId, sanityUri]
  );

  useEffect(() => {
    if (!lazy) {
      fetchPosData({
        restaurantPosDataId,
        storeNumber,
      });
    }
  }, [fetchPosData, lazy, restaurantPosDataId, storeNumber]);

  return useMemo(
    () => ({
      data,
      refetch: fetchPosData,
    }),
    [data, fetchPosData]
  );
};
